import React, { useState, useEffect } from 'react';

const CheckManagementForm = ({ formData, setFormData }) => {
  const [checkDetails, setCheckDetails] = useState({
    bank_number: '',
    branch_number: '',
    num_checks: 1,
    total_amount: formData.amount || '',
    divide_equally: true,
    first_check_number: '',
    first_check_date: new Date().toISOString().split('T')[0],
    checks: []
  });

  // Generate checks when auto-divide is enabled
  useEffect(() => {
    if (checkDetails.divide_equally && checkDetails.num_checks > 0 && checkDetails.total_amount) {
      const amount = parseFloat(checkDetails.total_amount);
      const numChecks = parseInt(checkDetails.num_checks);
      const amountPerCheck = (amount / numChecks).toFixed(2);
      const firstCheckNum = parseInt(checkDetails.first_check_number) || 1;
      const firstDate = new Date(checkDetails.first_check_date);

      const newChecks = [];
      for (let i = 0; i < numChecks; i++) {
        const checkDate = new Date(firstDate);
        checkDate.setMonth(checkDate.getMonth() + i); // Each check 1 month apart

        newChecks.push({
          check_number: (firstCheckNum + i).toString(),
          check_date: checkDate.toISOString().split('T')[0],
          amount: i === numChecks - 1 
            ? (amount - (amountPerCheck * (numChecks - 1))).toFixed(2) // Last check gets remainder
            : amountPerCheck
        });
      }
      setCheckDetails(prev => ({ ...prev, checks: newChecks }));
    }
  }, [
    checkDetails.divide_equally,
    checkDetails.num_checks,
    checkDetails.total_amount,
    checkDetails.first_check_number,
    checkDetails.first_check_date
  ]);

  // Update parent form data
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      bank_number: checkDetails.bank_number,
      branch_number: checkDetails.branch_number,
      checks: checkDetails.checks,
      amount: checkDetails.total_amount
    }));
  }, [checkDetails, setFormData]);

  const updateCheck = (index, field, value) => {
    const newChecks = [...checkDetails.checks];
    newChecks[index] = { ...newChecks[index], [field]: value };
    setCheckDetails({ ...checkDetails, checks: newChecks });
  };

  return (
    <div className="space-y-3 bg-gray-50 p-3 rounded-lg">
      <h3 className="text-base font-semibold text-gray-800">פרטי צ'קים</h3>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            מספר בנק *
          </label>
          <input
            type="text"
            value={checkDetails.bank_number}
            onChange={(e) => setCheckDetails({...checkDetails, bank_number: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="12"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            מספר סניף *
          </label>
          <input
            type="text"
            value={checkDetails.branch_number}
            onChange={(e) => setCheckDetails({...checkDetails, branch_number: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="001"
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            כמות צ'קים *
          </label>
          <input
            type="number"
            min="1"
            max="12"
            value={checkDetails.num_checks}
            onChange={(e) => setCheckDetails({...checkDetails, num_checks: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            סכום כולל *
          </label>
          <input
            type="number"
            step="0.01"
            value={checkDetails.total_amount}
            onChange={(e) => setCheckDetails({...checkDetails, total_amount: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="0.00"
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            מספר צ'ק ראשון *
          </label>
          <input
            type="text"
            value={checkDetails.first_check_number}
            onChange={(e) => setCheckDetails({...checkDetails, first_check_number: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="1000001"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            תאריך צ'ק ראשון *
          </label>
          <input
            type="date"
            value={checkDetails.first_check_date}
            onChange={(e) => setCheckDetails({...checkDetails, first_check_date: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            required
          />
        </div>
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="divide_equally"
          checked={checkDetails.divide_equally}
          onChange={(e) => setCheckDetails({...checkDetails, divide_equally: e.target.checked})}
          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
        />
        <label htmlFor="divide_equally" className="mr-2 block text-sm text-gray-700">
          חלק באופן שווה
        </label>
      </div>

      {checkDetails.checks.length > 0 && (
        <div className="mt-3">
          <h4 className="text-sm font-semibold text-gray-800 mb-2">צ'קים ({checkDetails.checks.length})</h4>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {checkDetails.checks.map((check, index) => (
              <div key={index} className="bg-white p-2 rounded-lg border border-gray-200">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      מספר צ'ק
                    </label>
                    <input
                      type="text"
                      value={check.check_number}
                      onChange={(e) => updateCheck(index, 'check_number', e.target.value)}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
                      disabled={checkDetails.divide_equally}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      תאריך
                    </label>
                    <input
                      type="date"
                      value={check.check_date}
                      onChange={(e) => updateCheck(index, 'check_date', e.target.value)}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
                      disabled={checkDetails.divide_equally}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      סכום (₪)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={check.amount}
                      onChange={(e) => updateCheck(index, 'amount', e.target.value)}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
                      disabled={checkDetails.divide_equally}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-2 p-2 bg-blue-50 rounded-lg">
            <div className="flex justify-between text-xs sm:text-sm">
              <span className="font-semibold text-gray-700">סה"כ צ'קים:</span>
              <span className="font-bold text-primary-600">
                ₪{checkDetails.checks.reduce((sum, c) => sum + parseFloat(c.amount || 0), 0).toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckManagementForm;

